import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Check, Flame, Gamepad2, CalendarDays } from "lucide-react";
import { api } from "../lib/api";

const ACCESS_LABEL = { free: "Free", connect: "Connect", premium: "Premium" };
const TIERS = ["free", "connect", "premium"];
const LOCK_TEXT = {
  date: "Unlocks on its day",
  tier: "Upgrade to unlock",
  spicy: "Opt in to Spicy to unlock",
};

const Badge = ({ children, className = "" }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[0.65rem] font-body uppercase tracking-wider ${className}`}>
    {children}
  </span>
);

function GameAnswer({ prompt, value, onChange }) {
  return (
    <div className="mt-5 rounded-xl border border-reveal-rose/20 bg-black/30 p-4">
      <div className="flex items-center gap-2 text-reveal-rose text-xs uppercase tracking-widest mb-2">
        <Gamepad2 className="w-4 h-4" /> Game
      </div>
      <p className="text-sm text-reveal-cream/75 leading-relaxed mb-3">{prompt}</p>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Your play..."
        data-testid="game-answer-input"
        className="w-full bg-black/40 border border-reveal-rose/30 rounded-lg px-3 py-2 text-sm text-reveal-cream outline-none focus:border-reveal-rose transition-colors"
      />
    </div>
  );
}

export default function DailyQuestions() {
  const [today, setToday] = useState(undefined);
  const [journey, setJourney] = useState(null);
  const [answer, setAnswer] = useState("");
  const [gameAnswer, setGameAnswer] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, j] = await Promise.all([
        api.get("/questions/today"),
        api.get("/questions/journey"),
      ]);
      setToday(t.data);
      setJourney(j.data);
      setAnswer(t.data?.my_answer?.answer || "");
      setGameAnswer(t.data?.my_answer?.game_answer || "");
    } catch {
      setToday(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/questions/answer", { day: today.day, answer, game_answer: gameAnswer });
      toast.success("Answer saved 🤍");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save answer");
    } finally {
      setSaving(false);
    }
  };

  const setTier = async (tier) => {
    try { await api.post("/user/tier", { tier }); await load(); toast.success(`Membership: ${ACCESS_LABEL[tier]}`); }
    catch { toast.error("Could not change membership"); }
  };

  const toggleSpicy = async () => {
    try { await api.post("/questions/spicy-optin", { opt_in: !journey.spicy_opt_in }); await load(); }
    catch { toast.error("Could not update Spicy setting"); }
  };

  if (today === undefined) return <p className="text-reveal-cream/50">Loading...</p>;
  if (today === null) return <p className="text-reveal-cream/50">Could not load questions.</p>;

  return (
    <div data-testid="daily-questions">
      {/* Membership + spicy controls (preview stub, no payment yet) */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-xs uppercase tracking-widest text-reveal-cream/40">Membership</span>
        <div className="flex gap-2" data-testid="tier-selector">
          {TIERS.map((tr) => (
            <button key={tr} onClick={() => setTier(tr)} data-testid={`tier-${tr}`}
              className={`px-3 py-1.5 rounded-full text-xs capitalize transition-colors ${
                journey?.tier === tr ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/70 hover:bg-reveal-rose/10"
              }`}>
              {ACCESS_LABEL[tr]}
            </button>
          ))}
        </div>
        <button onClick={toggleSpicy} data-testid="spicy-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors ${
            journey?.spicy_opt_in ? "bg-red-500/20 text-red-300 border border-red-500/40" : "border border-reveal-rose/25 text-reveal-cream/70 hover:bg-reveal-rose/10"
          }`}>
          <Flame className="w-3.5 h-3.5" /> Spicy {journey?.spicy_opt_in ? "on" : "off"}
        </button>
      </div>

      {/* Today's question card */}
      <div className="bg-reveal-burgundy/60 border border-reveal-rose/15 rounded-2xl p-6 mb-8" data-testid="today-card">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Badge className="bg-reveal-rose/15 text-reveal-rose">Day {today.day} of {today.total_days}</Badge>
          <Badge className="bg-white/5 text-reveal-cream/70">Level {today.level} · {today.level_name}</Badge>
          <Badge className="bg-white/5 text-reveal-cream/70">{ACCESS_LABEL[today.access]}</Badge>
          {today.spicy && <Badge className="bg-red-500/15 text-red-300"><Flame className="w-3 h-3" /> Spicy</Badge>}
        </div>

        {today.locked ? (
          <div className="flex flex-col items-center text-center py-8" data-testid="today-locked">
            <Lock className="w-8 h-8 text-reveal-rose/60 mb-3" />
            <p className="text-reveal-cream/70 font-body">{LOCK_TEXT[today.lock_reason] || "Locked"}</p>
          </div>
        ) : (
          <>
            <h3 className="font-heading text-2xl text-reveal-cream leading-snug mb-4" data-testid="today-question">
              {today.question}
            </h3>
            {today.options && (
              <div className="flex flex-wrap gap-2 mb-4">
                {today.options.map((o) => (
                  <button key={o} onClick={() => setAnswer(o)} type="button"
                    className="px-3 py-1.5 rounded-full text-xs border border-reveal-rose/30 text-reveal-cream/75 hover:bg-reveal-rose/10 transition-colors text-left">
                    {o}
                  </button>
                ))}
              </div>
            )}
            <textarea rows={4} value={answer} onChange={(e) => setAnswer(e.target.value)}
              placeholder="Take your time..." data-testid="answer-input"
              className="w-full bg-black/40 border border-reveal-rose/40 rounded-xl px-4 py-3 font-body text-reveal-cream placeholder:text-reveal-cream/40 outline-none focus:border-reveal-rose transition-colors resize-none" />

            {today.game_prompt && <GameAnswer prompt={today.game_prompt} value={gameAnswer} onChange={setGameAnswer} />}

            <div className="flex items-center gap-3 mt-5">
              <button onClick={submit} disabled={saving || !answer.trim()} data-testid="submit-answer-button"
                className="bg-reveal-rose text-black font-medium px-6 py-2.5 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-60">
                {saving ? "Saving..." : today.answered ? "Update Answer" : "Submit Answer"}
              </button>
              {today.answered && <span className="text-sm text-green-400 inline-flex items-center gap-1"><Check className="w-4 h-4" /> Answered</span>}
            </div>
          </>
        )}
      </div>

      {/* 30-day journey */}
      <div className="mb-3 flex items-center gap-2 text-reveal-cream/60 text-sm">
        <CalendarDays className="w-4 h-4 text-reveal-rose" /> Your 30-day journey
      </div>
      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2" data-testid="journey-grid">
        {journey?.days.map((d) => (
          <div key={d.day} data-testid={`journey-day-${d.day}`}
            title={d.locked ? (LOCK_TEXT[d.lock_reason] || "Locked") : `Day ${d.day}`}
            className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-xs border transition-colors ${
              d.day === journey.current_day ? "border-reveal-rose bg-reveal-rose/15 text-reveal-cream" :
              d.answered ? "border-green-500/40 bg-green-500/10 text-green-300" :
              d.locked ? "border-reveal-rose/10 bg-black/30 text-reveal-cream/35" :
              "border-reveal-rose/25 bg-white/[0.02] text-reveal-cream/70"
            }`}>
            <span className="font-heading text-sm">{d.day}</span>
            {d.answered ? <Check className="w-3 h-3" /> : d.locked ? <Lock className="w-3 h-3" /> : null}
            {d.spicy && <Flame className="w-2.5 h-2.5 absolute top-1 right-1 text-red-300/80" />}
          </div>
        ))}
      </div>
      <p className="text-xs text-reveal-cream/40 mt-4">
        One question unlocks each day. Membership controls above are a preview (billing arrives in a later phase).
      </p>
    </div>
  );
}
